/**
 * Access control via hashed emails.
 * Emails are never stored in plain text — only their SHA-256 hashes.
 * To add a user: node -e "const c=require('crypto');console.log(c.createHash('sha256').update('email@allianz.com').digest('hex'))"
 */

function sha256sync(str) {
  const K = [
    0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
    0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
    0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
    0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
    0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
    0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
    0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
    0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2,
  ];
  const rotr = (n, x) => (x >>> n) | (x << (32 - n));
  const s0 = x => rotr(2,x) ^ rotr(13,x) ^ rotr(22,x);
  const s1 = x => rotr(6,x) ^ rotr(11,x) ^ rotr(25,x);
  const g0 = x => rotr(7,x) ^ rotr(18,x) ^ (x >>> 3);
  const g1 = x => rotr(17,x) ^ rotr(19,x) ^ (x >>> 10);
  const ch  = (x,y,z) => (x & y) ^ (~x & z);
  const maj = (x,y,z) => (x & y) ^ (x & z) ^ (y & z);

  const bytes = new TextEncoder().encode(str);
  const len = bytes.length;
  const paddedLen = Math.ceil((len + 9) / 64) * 64;
  const padded = new Uint8Array(paddedLen);
  padded.set(bytes);
  padded[len] = 0x80;
  const dv = new DataView(padded.buffer);
  dv.setUint32(paddedLen - 4, len * 8, false);

  let h = [0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19];
  const w = new Uint32Array(64);

  for (let i = 0; i < paddedLen; i += 64) {
    for (let j = 0; j < 16; j++) w[j] = dv.getUint32(i + j * 4, false);
    for (let j = 16; j < 64; j++) w[j] = (g1(w[j-2]) + w[j-7] + g0(w[j-15]) + w[j-16]) | 0;
    let [a,b,c,d,e,f,g,hh] = h;
    for (let j = 0; j < 64; j++) {
      const t1 = (hh + s1(e) + ch(e,f,g) + K[j] + w[j]) | 0;
      const t2 = (s0(a) + maj(a,b,c)) | 0;
      hh=g; g=f; f=e; e=(d+t1)|0; d=c; c=b; b=a; a=(t1+t2)|0;
    }
    h = h.map((v,i) => (v + [a,b,c,d,e,f,g,hh][i]) | 0);
  }
  return h.map(v => (v >>> 0).toString(16).padStart(8,'0')).join('');
}

// SHA-256 hashes of authorised emails (all lowercased before hashing)
export const ODM_ADMIN_USERS = [
  '3b13d9783937b8efe32dab108cd7525a1de01fc46a03499bf83a5e3431d993f9', // akshay.deshmukh@allianz.com
  '2a3fa99466ff4f9fc5c3dcf96f9a146774c70318470dd0f507e2949e7554b0e5', // ankita.singh@allianz.com
  'e540eaec3e5fdd879ed66d9c5ce4cc9429cf38fdedf8fa50c4f25c4c6f4d7d8f', // jane.smith@allianz.com
  '90b107bed1639fce29e19dd14868c25dd7eb3472515b43bf68d33b40f6da19f4', // retheesh_olangattu@allianz.com
  'dd6ee9658eb87308bb118286f06835356c908e7919f591787b83966f11244cde', // admin@allianz.com
  'adcc1be0f9b46269f099a1109f4ce0a00edc981a76ed123d91f83a9eb9c4c8f9', // alin-marian.david@allianz.com
];

export const CIRCUIT_ORDER_USERS = [
  '2a3fa99466ff4f9fc5c3dcf96f9a146774c70318470dd0f507e2949e7554b0e5', // ankita.singh@allianz.com
  'f151c36fb406d7aeacea8d3c62db80246e142ecd741a87ecc84e16716d752987', // anisha.rajkumar-ghundiyal@allianz.com
];

/**
 * Returns true if the given email is in the specified hashed allow-list.
 * Synchronous — safe to call directly in render, no useEffect needed.
 */
export function isEmailAllowed(email, hashList) {
  if (!email) return false;
  return hashList.includes(sha256sync(email.toLowerCase()));
}
